import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import { useEffect, useRef, useState, forwardRef, useContext } from 'react'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import PropTypes from 'prop-types'

// Internal Utils
import { throttle } from '../../utils/helpers/throttle'

// Internal components
import Svg from '../Svg/Svg'
import { UserContext } from '../../context/UserContext'

gsap.registerPlugin(ScrollTrigger)

const SiteLogo = forwardRef((props, [ScrollObserverRef, containerRef]) => {
  // consts
  const breakpoints = [0, 768, 930, 1580]
  const theme = process.env.NEXT_PUBLIC_THEME
  const coordinatesObj = {
    sportwide: {
      backdrop: '42,0 84,0 126,63 63,126 0,63',
      link: '{"0":{"scale":1,"x":0,"y":0}, "768":{"scale":0.48,"x":0,"y":22}, "930":{"scale":0.44,"x":-10,"y":7},"1580":{"scale":0.3828,"x":21,"y":6}}',
      figure:
        '{"0":{"x1":0,"y1":40,"x2":103,"y2":40,"x3":141,"y3":126,"x4":64,"y4":126,"x5":0,"y5":126}, "768":{"x1":0,"y1":40,"x2":103,"y2":40,"x3":141,"y3":127,"x4":64,"y4":127,"x5":0,"y5":127},"930":{"x1":0,"y1":40,"x2":93,"y2":40,"x3":131,"y3":97,"x4":63,"y4":97,"x5":0,"y5":97},"1580":{"x1":24,"y1":40,"x2":102,"y2":40,"x3":138,"y3":97,"x4":63,"y4":97,"x5":24,"y5":40}}'
    },
    worldcoaches: {
      backdrop: '35,0 105,0 140,60.6218 105,121.244 35,121.244 0,60.6218 35,0',
      link: '{"0":{"scale":1,"x":0,"y":0}, "768":{"scale":0.48,"x":0,"y":20}, "930":{"scale":0.44,"x":-10,"y":13},"1580":{"scale":0.3828,"x":11,"y":12}}',
      figure:
        '{"0":{"x1":0,"y1":40,"x2":103,"y2":40,"x3":141,"y3":126,"x4":64,"y4":126,"x5":0,"y5":126,"x6":0,"y6":126,"x7":0,"y7":126}, "768":{"x1":0,"y1":40,"x2":103,"y2":40,"x3":141,"y3":137,"x4":64,"y4":137,"x5":0,"y5":137,"x6":0,"y6":137,"x7":0,"y7":137},"930":{"x1":0,"y1":40,"x2":93,"y2":40,"x3":131,"y3":107,"x4":63,"y4":107,"x5":0,"y5":107,"x6":0,"y6":107,"x7":0,"y7":97},"1580":{"x1":24,"y1":40,"x2":102,"y2":40,"x3":138,"y3":107,"x4":63,"y4":107,"x5":24,"y5":40,"x6":24,"y6":40,"x7":24,"y7":40}}'
    },
    louis: {
      backdrop: '42,0 84,0 126,63 63,126 0,63',
      link: '{"0":{"scale":1,"x":0,"y":0}, "768":{"scale":0.48,"x":0,"y":22}, "930":{"scale":0.44,"x":-10,"y":7},"1580":{"scale":0.3828,"x":21,"y":6}}',
      figure:
        '{"0":{"x1":0,"y1":40,"x2":103,"y2":40,"x3":141,"y3":126,"x4":64,"y4":126,"x5":0,"y5":126}, "768":{"x1":0,"y1":40,"x2":103,"y2":40,"x3":141,"y3":127,"x4":64,"y4":127,"x5":0,"y5":127},"930":{"x1":0,"y1":40,"x2":93,"y2":40,"x3":131,"y3":97,"x4":63,"y4":97,"x5":0,"y5":97},"1580":{"x1":24,"y1":40,"x2":102,"y2":40,"x3":138,"y3":97,"x4":63,"y4":97,"x5":24,"y5":40}}'
    },
    knvb: {
      backdrop: '42,0 84,0 126,63 63,126 0,63',
      link: '{"0":{"scale":1,"x":0,"y":0}, "768":{"scale":0.48,"x":0,"y":22}, "930":{"scale":0.44,"x":-10,"y":7},"1580":{"scale":0.3828,"x":21,"y":6}}',
      figure:
        '{"0":{"x1":0,"y1":40,"x2":103,"y2":40,"x3":141,"y3":126,"x4":64,"y4":126,"x5":0,"y5":126}, "768":{"x1":0,"y1":40,"x2":103,"y2":40,"x3":141,"y3":127,"x4":64,"y4":127,"x5":0,"y5":127},"930":{"x1":0,"y1":40,"x2":93,"y2":40,"x3":131,"y3":97,"x4":63,"y4":97,"x5":0,"y5":97},"1580":{"x1":24,"y1":40,"x2":102,"y2":40,"x3":138,"y3":97,"x4":63,"y4":97,"x5":24,"y5":40}}'
    },
    frf: {
      backdrop: '22,0 93,0 115,68 57,110 0,68',
      link: '{"0":{"scale":1,"x":0,"y":0}, "768":{"scale":0.49,"x":0,"y":22}, "930":{"scale":0.4828,"x":-10,"y":16},"1580":{"scale":0.44,"x":23,"y":15}}',
      figure:
        '{"0":{"x1":0,"y1":40,"x2":103,"y2":40,"x3":141,"y3":126,"x4":64,"y4":126,"x5":0,"y5":126}, "768":{"x1":0,"y1":40,"x2":89,"y2":40,"x3":120,"y3":110.5,"x4":64,"y4":110.5,"x5":0,"y5":110.5},"930":{"x1":0,"y1":40,"x2":93,"y2":40,"x3":131,"y3":97,"x4":63,"y4":97,"x5":0,"y5":97},"1580":{"x1":19,"y1":40,"x2":102,"y2":40,"x3":138,"y3":97,"x4":58,"y4":97,"x5":19,"y5":40}}'
    },
    fmf: {
      d: 'M62.9056 120.997L62.2643 120.711C61.8378 120.522 51.7193 115.939 40.24 106.084C33.4944 100.291 27.6961 93.8766 23.0048 87.0168C17.1284 78.4235 12.9873 69.1048 10.6921 59.3242C7.73597 46.7366 8.475 34.3019 9.61772 26.0859C10.8516 17.19 12.7626 11.3256 12.844 11.0816L13.4268 9.3122L15.0611 10.2034C15.1002 10.2262 19.212 12.4347 25.2577 13.6512C33.273 15.2645 40.7153 14.4643 47.3763 11.2735C55.4796 7.39316 61.7368 1.09287 61.7987 1.03107L62.8177 0L63.9181 0.946507C63.9865 1.00505 70.8233 6.876 74.424 9.04223C78.6563 11.5858 83.2011 13.2381 87.9283 13.9472C91.7145 14.5131 95.6343 14.4838 99.5736 13.8561C106.283 12.7892 110.718 10.3563 110.763 10.3335L112.42 9.41303L112.993 11.2182C113.071 11.4589 114.885 17.2225 116.027 26.0208C117.079 34.1393 117.714 46.4602 114.764 59.0412C112.469 68.8185 108.347 78.1502 102.507 86.7729C97.8481 93.6554 92.0889 100.102 85.3954 105.934C74.004 115.861 63.9669 120.512 63.547 120.707L62.9089 121L62.9056 120.997Z',
      link: '{"0":{"scale":1,"x":0,"y":0}, "768":{"scale":0.45,"x":-14,"y":17}, "930":{"scale":0.43,"x":-14,"y":8},"1580":{"scale":0.4,"x":20,"y":6}}',
      figure:
        '{"0":{"d":"M52.9053 111.5L49.3267 111.5C40.2924 111.5 31.1991 111.5 19.2397 111.5C11.4968 111.5 9.97733 111.5 0.999763 111.5C0.995304 101.153 1.0011 71.224 0.999763 61.3242C0.999763 48.367 0.999763 49.8014 0.999763 41C3.23055 41 4.34478 41 4.84371 41H5.42647H7.06078C11.4668 41 10.7664 41 17.2574 41C25.5532 41.0001 32.0317 41 39.376 41C49.0514 40.9999 51.1307 41 53.7984 40.9999H54.8174H55.9178C55.9862 41.0584 61.496 40.9999 66.4237 40.9999C71.3513 40.9999 83.1225 40.9553 89.9547 40.9553C91.136 43.7648 91.7209 44.9451 92.3173 46.287C95.6376 53.7578 107.226 80.0807 108.134 82.4337L109.613 85.5936L111.227 89.3922C112.101 91.1738 114.175 95.3345 115.535 98.3458C118.027 104.576 118.66 105.988 120.98 111.5C109.215 111.5 106.675 111.5 95.1119 111.5C88.7919 111.5 85.3132 111.5 78.0003 111.5C61.8551 111.5 61.1895 111.5 59.6308 111.5L55.9086 111.5H52.9053Z"}, "768":{"d":"M52.9053 111.5L49.3267 111.5C40.2924 111.5 31.1991 111.5 19.2397 111.5C11.4968 111.5 9.97733 111.5 0.999763 111.5C0.995304 101.153 1.0011 71.224 0.999763 61.3242C0.999763 48.367 0.999763 49.8014 0.999763 41C3.23055 41 4.34478 41 4.84371 41H5.42647H7.06078C11.4668 41 10.7664 41 17.2574 41C25.5532 41.0001 32.0317 41 39.376 41C49.0514 40.9999 51.1307 41 53.7984 40.9999H54.8174H55.9178C55.9862 41.0584 61.496 40.9999 66.4237 40.9999C71.3513 40.9999 83.1225 40.9553 89.9547 40.9553C91.136 43.7648 91.7209 44.9451 92.3173 46.287C95.6376 53.7578 107.226 80.0807 108.134 82.4337L109.613 85.5936L111.227 89.3922C112.101 91.1738 114.175 95.3345 115.535 98.3458C118.027 104.576 118.66 105.988 120.98 111.5C109.215 111.5 106.675 111.5 95.1119 111.5C88.7919 111.5 85.3132 111.5 78.0003 111.5C61.8551 111.5 61.1895 111.5 59.6308 111.5L55.9086 111.5H52.9053Z"},"930":{"d": "M52.9053 97.5L49.3267 97.4999C40.2924 97.5 31.1991 97.4999 19.2397 97.4999C11.4968 97.4999 9.97733 97.4999 0.999763 97.4999C0.995304 87.1529 1.0011 71.224 0.999763 61.3242C0.999763 48.367 0.999763 48.8014 0.999763 40C3.23055 40 4.34478 40 4.84371 40H5.42647H7.06078C11.4668 40 10.7664 40 17.2574 40C25.5532 40.0001 32.0317 40 39.376 40C49.0514 39.9999 51.1307 40 53.7984 39.9999H54.8174H55.9178C55.9862 40.0584 61.496 39.9999 66.4237 39.9999C71.3513 39.9999 87.1225 39.9553 93.9547 39.9553C95.6994 42.6647 97.5249 44.9772 98.1213 46.3191C103.024 53.8124 118.335 76.5035 122.184 82.3209L124.355 85.5625L126.931 89.4217C128.068 91.094 128.977 92.5327 129.535 93.3458C130.159 94.2554 131.211 95.8186 132.304 97.4909C120.539 97.4909 106.675 97.4999 95.1119 97.4999C88.7919 97.4999 85.3132 97.4999 78.0003 97.4999C61.8551 97.4999 61.1895 97.4999 59.6308 97.4999L55.9086 97.5H52.9053Z"},"1580":{"d": "M63.9789 97.4689L63.7571 97.0969C63.1276 96.1025 59.4654 90.5842 53.0905 80.8701C46.4879 71.6872 45.4018 69.8766 40.7105 63.0169C35.8016 55.8391 34.8642 54.4214 32.6918 51.3242C30.1562 47.3427 28.312 44.8733 27.0796 43.0405C26.2211 41.7645 25.1934 40.7215 25.2748 40.4775L25.0146 40.0319L25.6627 40.0319C25.7018 40.0547 33.9721 40.0319 39.7184 39.9932C45.1214 39.9932 47.4108 39.9931 50.2267 39.9931C56.431 39.9931 61.4905 39.9931 61.7985 40.0311L62.8175 40L64.2381 40.0311C64.3065 40.0897 69.1142 40.0422 74.4238 40.0422C79.7334 40.0422 83.1023 39.9471 87.9281 39.9471C92.6262 39.9471 95.2944 40.0442 99.4943 39.9471C102.781 39.9472 104.668 39.9532 104.773 39.9532L105.955 39.9532L106.49 40.7889C106.569 41.0296 115.182 54.4413 121.685 64.7731C125.282 70.4879 132.773 82.3082 142.233 97.3941C132.05 97.3941 124.161 97.4569 111.998 97.4569C102.444 97.4569 94.5241 97.4569 85.2211 97.4569C74.661 97.4569 66.2609 97.5118 64.9518 97.5118H64.0218L63.9789 97.4689Z"}}'
    }
  }

  // refs
  let datasetRef = useRef(null)
  let activeBreakpointRef = useRef(null)
  const siteLogoRef = useRef(null)
  const backdropRef = useRef(null)
  const linkRef = useRef(null)
  const figureRef = useRef(null)

  const userContext = useContext(UserContext)

  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )

  // eslint-disable-next-line no-unused-vars
  const [activeWidth, setActiveWidth] = useState(false)
  const [menuFolded, setMenuFolded] = useState(false)
  const [timeline] = useState(() => gsap.timeline({ paused: true }))

  const getActiveBreakpoint = () => {
    let bp = ''
    Object.values(breakpoints).forEach((value) => {
      bp = window.innerWidth >= parseInt(value) ? value : bp
    })
    return bp
  }

  const getFigureDataset = (ref) => {
    const dataset = JSON.parse(ref.current.dataset.pointsFixed)
    return dataset
  }

  const getLinkDataset = (ref) => {
    const dataset = JSON.parse(ref.current.dataset.transformFixed)
    return dataset
  }

  const getDataset = () => {
    const figureData = getFigureDataset(figureRef)
    const linkData = getLinkDataset(linkRef)
    const dataset = { link: linkData[activeBreakpointRef], figure: figureData[activeBreakpointRef] }
    return dataset
  }

  const setAnimationProperties = () => {
    timeline.restart()
    timeline.pause()
    timeline.clear(true)

    datasetRef = getDataset()

    ScrollTrigger.create({
      trigger: ScrollObserverRef.current,
      animation: timeline,
      start: 'bottom',
      end: 'top',
      onEnter: () => setMenuFolded(true),
      onEnterBack: () => setMenuFolded(false)
    })

    const points = datasetRef.figure
    const transform = datasetRef.link
    const offset = activeBreakpointRef === 0 ? -50 : -44

    if (points?.d) {
      timeline.to(
        backdropRef.current,
        {
          duration: 0.6,
          delay: 0.2,
          attr: {
            d: `${points.d}`
          },
          ease: 'power3.inOut'
        },
        0
      )
    } else {
      timeline.to(
        backdropRef.current,
        {
          duration: 0.6,
          delay: 0.2,
          attr: {
            points: `${points.x1},${points.y1} ${points.x2},${points.y2} ${points.x3},${points.y3} ${points.x4},${
              points.y4
            } ${points.x5},${points.y5}${points.x6 ? ` ${points.x6},${points.y6}` : ''}${
              points.x7 ? ` ${points.x7},${points.y7}` : ''
            }`
          },
          ease: 'power3.inOut'
        },
        0
      )
    }
    timeline.to(containerRef.current, { duration: 0.8, delay: 0, y: offset, ease: 'power3.inOut' }, 0)
    timeline.to(
      linkRef.current,
      { duration: 0.6, delay: 0.2, scale: transform.scale, x: transform.x, y: transform.y, ease: 'power3.inOut' },
      0
    )
  }

  const reCalculateBreakpoints = throttle((e) => {
    const activeBp = getActiveBreakpoint()
    const currentBp = activeBreakpointRef

    if (parseInt(currentBp) !== parseInt(activeBp)) {
      setActiveWidth(activeBp)
      activeBreakpointRef = activeBp
      setAnimationProperties()
    }
  }, 100)

  useEffect(() => {
    const activeBp = getActiveBreakpoint()
    setActiveWidth(activeBp)
    activeBreakpointRef = activeBp
    setAnimationProperties()
  }, [])

  useEffect(() => {
    menuFolded === true ? timeline.play() : timeline.reverse()
  }, [menuFolded])

  useEffect(() => {
    window.addEventListener('resize', reCalculateBreakpoints)
    return () => window.removeEventListener('resize', reCalculateBreakpoints)
  }, [])

  return (
    <div className="SiteLogo" ref={siteLogoRef}>
      <div className="SiteLogo-foreground">
        <Link href="/">
          <a className="SiteLogo-link" ref={linkRef} data-transform-fixed={coordinatesObj[theme].link}>
            <Svg id={`logo-${theme}`} modifiers={['SiteLogo-image']} />
            <Svg id={`logo-${theme}--mobile`} modifiers={['SiteLogo-image--mobile']} />
            <span className="SiteLogo-label">{t('SITELOGO__LABEL')}</span>
          </a>
        </Link>
      </div>
      <div className={`SiteLogo-background ${menuFolded ? 'is-folded' : ''}`}>
        <div className="SiteLogo-figure" ref={figureRef} data-points-fixed={coordinatesObj[theme].figure}>
          <svg className="SiteLogo-svg" viewBox="0 0 143 126" preserveAspectRatio="xMidYMin slice">
            {coordinatesObj[theme].backdrop ? (
              <polygon className="SiteLogo-svgBackdrop" ref={backdropRef} points={coordinatesObj[theme].backdrop} />
            ) : (
              <path className="SiteLogo-svgBackdrop" ref={backdropRef} d={coordinatesObj[theme].d} />
            )}
          </svg>
        </div>
      </div>
    </div>
  )
})

SiteLogo.propTypes = {
  name: PropTypes.string
}

export default SiteLogo
